import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost, apiDelete } from "../../../services/api";
import { useClinicStore } from "../../../store/clinicStore"; // Import store for diagnoses
import { useToast } from "../../common/ToastProvider";
import Button from "../../common/Button";
import "./DoctorReview.css";

interface ProcedureSessionRecord {
  id: number;
  procedure_type?: string;
  tooth_no?: string | null;
  target_ref?: string | null;
  status?: string;
  step_no?: number | null;
  total_steps?: number | null;
  current_step?: string | null;
}

interface ChartHistoryRecord {
  tooth?: string | null;
  multi_tooth?: string | null;
  procedure?: string | null;
  step_label?: string | null;
}

interface ClinicalRecord {
  clinical_id: number;
  procedure_name?: string;
  tooth_number?: string;
  treatment_status?: string;
  step_no?: number | null;
  total_steps?: number | null;
  steps?: string[];
}

interface Prescription {
  id: number;
  medicines?: { drug_name?: string; name?: string; frequency?: string; dosage?: string; days?: string; instructions?: string }[];
}

interface LabItem {
  id: number;
  name: string;
  contact?: string;
}

interface LabWork {
  id: number;
  name: string;
}

interface LabOrder {
  id: number;
  visit_id?: number;
  patient_id?: string;
  lab_id?: number;
  work_id?: number;
  lab_name?: string;
  work_name?: string;
  impression_date?: string | null;
  eta_date?: string | null;
  status?: string;
  created_at?: string;
  received_at?: string | null;
}

interface TreatmentSignoff {
  visit_id?: number;
  signed_by?: string;
  signed_by_id?: number;
  signed_role?: string;
  notes?: string | null;
  signed_at?: string | null;
  updated_at?: string | null;
}

interface CommunicationLog {
  id: number;
  patient_id?: string;
  visit_id?: number;
  channel?: string;
  direction?: string;
  subject?: string;
  notes?: string;
  created_by_name?: string;
  created_by_role?: string;
  created_at?: string;
}

interface VisitAttachment {
  id: number;
  url?: string;
  thumbnail_url?: string;
  filename?: string;
  created_at?: string;
  tooth_number?: string | null;
  procedure_type?: string | null;
  notes?: string | null;
}

interface Patient {
  name: string;
  age?: number;
  gender?: string;
  patient_id?: string | number;
}

interface Visit {
  visit_id?: number;
  created_at?: string;
}

interface ClinicalNotesData {
  complaints?: string;
  habits?: string;
  observation?: string;
}

interface DoctorReviewProps {
  patient: Patient | null;
  visit: Visit | null;
  onModifyChart: () => void;
  onLockSuccess?: () => void;
}

export default function DoctorReview({ patient, visit, onModifyChart, onLockSuccess }: DoctorReviewProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const visitId = visit?.visit_id;
  const { sessionDiagnoses } = useClinicStore(); // Get diagnoses from store

  const [records, setRecords] = useState<ClinicalRecord[]>([]);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [clinicalNotes, setClinicalNotes] = useState<ClinicalNotesData>({});
  const [loading, setLoading] = useState(true);
  const [locking, setLocking] = useState(false);
  const [recallDate, setRecallDate] = useState("");
  const [recallReason, setRecallReason] = useState("");
  const [labs, setLabs] = useState<LabItem[]>([]);
  const [labWorks, setLabWorks] = useState<LabWork[]>([]);
  const [labOrders, setLabOrders] = useState<LabOrder[]>([]);
  const [labSaving, setLabSaving] = useState(false);
  const [labForm, setLabForm] = useState({
    lab_id: "",
    work_id: "",
    impression_date: "",
    eta_date: "",
  });

  const [signoff, setSignoff] = useState<TreatmentSignoff | null>(null);
  const [signoffNotes, setSignoffNotes] = useState("");
  const [signing, setSigning] = useState(false);

  const [commLogs, setCommLogs] = useState<CommunicationLog[]>([]);
  const [commSaving, setCommSaving] = useState(false);
  const [commForm, setCommForm] = useState({
    channel: "CALL",
    direction: "OUT",
    subject: "",
    notes: "",
  });

  const [attachments, setAttachments] = useState<VisitAttachment[]>([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [attachmentMeta, setAttachmentMeta] = useState({
    tooth_number: "",
    procedure_type: "",
    notes: "",
  });

  const loadData = useCallback(() => {
    if (!visitId) return;
    setLoading(true);
    Promise.all([
      apiGet(`/api/doctor/procedures/by-visit/${visitId}`) as Promise<ProcedureSessionRecord[]>,
      apiGet(`/api/doctor/chart-history/${visitId}`) as Promise<ChartHistoryRecord[]>,
      apiGet(`/api/doctor/prescriptions/visit/${visitId}`) as Promise<Prescription[]>,
      apiGet(`/api/visits/${visitId}`) as Promise<Record<string, unknown>> // Fetch visit details for notes
    ])
      .then(([sessions, history, rx, visitDetails]) => {
        const historyList = Array.isArray(history) ? history : [];
        const stepMap = new Map<string, string[]>();
        historyList.forEach((h) => {
          const key = `${(h.procedure || "").toLowerCase()}|${(h.tooth || h.multi_tooth || "").toLowerCase()}`;
          if (!key) return;
          if (!stepMap.has(key)) stepMap.set(key, []);
          const label = (h.step_label || "").trim();
          if (label && !stepMap.get(key)!.includes(label)) {
            stepMap.get(key)!.push(label);
          }
        });

        const sessionList = Array.isArray(sessions) ? sessions : [];
        const mapped: ClinicalRecord[] = sessionList.map((s) => {
          const procName = s.procedure_type || "Procedure";
          const toothRef = s.tooth_no || s.target_ref || "";
          const key = `${procName.toLowerCase()}|${String(toothRef).toLowerCase()}`;
          const steps = stepMap.get(key) || [];
          const rawStatus = (s.status || "").toString().toLowerCase();
          const treatment_status =
            rawStatus.includes("complete") ? "Completed" :
              rawStatus.includes("plan") ? "Planned" :
                rawStatus.includes("progress") || rawStatus.includes("in_progress") ? "In Progress" :
                  rawStatus ? s.status : "Planned";
          return {
            clinical_id: s.id,
            procedure_name: procName,
            tooth_number: toothRef,
            treatment_status,
            step_no: s.step_no ?? null,
            total_steps: s.total_steps ?? null,
            steps
          };
        });

        setRecords(mapped);
        setPrescriptions(Array.isArray(rx) ? rx : []);

        // Extract clinical notes
        if (visitDetails) {
          setClinicalNotes({
            complaints: (visitDetails.complaints as string) || "",
            habits: (visitDetails.habits as string) || "",
            observation: (visitDetails.observation as string) || ""
          });
        }

      })
      .catch((e) => console.error("DoctorReview load failed", e))
      .finally(() => setLoading(false));
  }, [visitId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadLabData = useCallback(() => {
    if (!visitId) return;
    Promise.all([
      apiGet("/api/doctor/labs") as Promise<LabItem[]>,
      apiGet("/api/doctor/lab-works") as Promise<LabWork[]>,
      apiGet(`/api/doctor/lab-orders?visit_id=${visitId}`) as Promise<LabOrder[]>,
    ])
      .then(([labsRes, worksRes, ordersRes]) => {
        setLabs(Array.isArray(labsRes) ? labsRes : []);
        setLabWorks(Array.isArray(worksRes) ? worksRes : []);
        setLabOrders(Array.isArray(ordersRes) ? ordersRes : []);
      })
      .catch(() => {
        setLabs([]);
        setLabWorks([]);
        setLabOrders([]);
      });
  }, [visitId]);

  const loadSignoff = useCallback(() => {
    if (!visitId) return;
    apiGet(`/api/doctor/treatment-signoff/${visitId}`)
      .then((data: unknown) => {
        setSignoff((data as TreatmentSignoff) || null);
      })
      .catch(() => setSignoff(null));
  }, [visitId]);

  const loadComms = useCallback(() => {
    const pid = patient?.patient_id ? String(patient.patient_id) : "";
    if (!pid) return;
    apiGet(`/api/communication?patient_id=${encodeURIComponent(pid)}&limit=20`)
      .then((data: unknown) => {
        setCommLogs(Array.isArray(data) ? (data as CommunicationLog[]) : []);
      })
      .catch(() => setCommLogs([]));
  }, [patient?.patient_id]);

  const loadAttachments = useCallback(() => {
    if (!visitId) return;
    apiGet(`/api/doctor/attachments/visit/${visitId}`)
      .then((data: unknown) => {
        setAttachments(Array.isArray(data) ? (data as VisitAttachment[]) : []);
      })
      .catch(() => setAttachments([]));
  }, [visitId]);

  useEffect(() => {
    loadLabData();
  }, [loadLabData]);

  useEffect(() => {
    loadSignoff();
  }, [loadSignoff]);

  useEffect(() => {
    loadComms();
  }, [loadComms]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const completedItems = useMemo(
    () => records.filter((r) => r.treatment_status === "Completed"),
    [records]
  );
  const inProgressItems = useMemo(
    () => records.filter((r) => r.treatment_status === "In Progress"),
    [records]
  );
  const plannedItems = useMemo(
    () => records.filter((r) => r.treatment_status === "Planned"),
    [records]
  );
  const allCompleted = records.length > 0 && records.every((r) => r.treatment_status === "Completed");
  const procLabel = (r: ClinicalRecord) =>
    r.procedure_name || "Unspecified";
  const estimatedTotal = useMemo(
    () => completedItems.length * 500,
    [completedItems]
  );

  // Calculate duration
  const duration = useMemo(() => {
    if (!visit?.created_at) return null;
    const start = new Date(visit.created_at).getTime();
    const end = new Date().getTime();
    const diff = Math.max(0, end - start);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  }, [visit?.created_at]);

  const resolveBlocker = async (recordId: number, newStatus: string) => {
    try {
      await apiPost(`/api/doctor/clinical-records/${recordId}/status`, {
        status: newStatus
      });
      loadData();
    } catch (e) {
      console.error("Failed to update status", e);
    }
  };

  const submitRecall = async () => {
    if (!recallDate || !patient?.patient_id) return;
    await apiPost("/api/recalls", {
      patient_id: String(patient.patient_id),
      recall_date: recallDate,
      reason: recallReason || "Next recall",
    });
  };

  const handleCreateLabOrder = async () => {
    if (!visitId || labSaving) return;
    if (!labForm.lab_id || !labForm.work_id) {
      showToast({ message: "Select lab and work type.", type: "error" });
      return;
    }
    setLabSaving(true);
    try {
      await apiPost("/api/doctor/lab-orders", {
        visit_id: visitId,
        lab_id: Number(labForm.lab_id),
        work_id: Number(labForm.work_id),
        impression_date: labForm.impression_date || null,
        eta_date: labForm.eta_date || null,
      });
      setLabForm({ lab_id: "", work_id: "", impression_date: "", eta_date: "" });
      loadLabData();
      showToast({ message: "Lab order created.", type: "success" });
    } catch (e) {
      showToast({ message: "Failed to create lab order.", type: "error" });
    } finally {
      setLabSaving(false);
    }
  };

  const handleMarkReceived = async (orderId: number) => {
    try {
      await apiPost(`/api/doctor/lab-orders/${orderId}/status`, { status: "RECEIVED" });
      loadLabData();
      showToast({ message: "Lab order marked as received.", type: "success" });
    } catch (e) {
      showToast({ message: "Failed to update lab order.", type: "error" });
    }
  };

  const handleSignoff = async () => {
    if (!visitId || signing) return;
    setSigning(true);
    try {
      const res = await apiPost("/api/doctor/treatment-signoff", {
        visit_id: visitId,
        notes: signoffNotes || null,
      });
      setSignoff(res as TreatmentSignoff);
      setSignoffNotes("");
      showToast({ message: "Treatment signed off.", type: "success" });
    } catch (e) {
      showToast({ message: "Failed to sign off treatment.", type: "error" });
    } finally {
      setSigning(false);
    }
  };

  const handleAddCommunication = async () => {
    const pid = patient?.patient_id ? String(patient.patient_id) : "";
    if (!pid || commSaving) return;
    if (!commForm.notes.trim() && !commForm.subject.trim()) {
      showToast({ message: "Add a subject or notes.", type: "error" });
      return;
    }
    setCommSaving(true);
    try {
      await apiPost("/api/communication", {
        patient_id: pid,
        visit_id: visitId || null,
        channel: commForm.channel,
        direction: commForm.direction,
        subject: commForm.subject,
        notes: commForm.notes,
      });
      setCommForm({ channel: "CALL", direction: "OUT", subject: "", notes: "" });
      loadComms();
      showToast({ message: "Communication logged.", type: "success" });
    } catch (e) {
      showToast({ message: "Failed to log communication.", type: "error" });
    } finally {
      setCommSaving(false);
    }
  };

  const fileToBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        const base64 = result.includes(",") ? result.split(",")[1] : result;
        resolve(base64);
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });

  const handleAttachmentUpload = async (file: File) => {
    if (!visitId || attachmentUploading) return;
    setAttachmentUploading(true);
    try {
      const base64 = await fileToBase64(file);
      await apiPost(`/api/doctor/attachments/visit/${visitId}/upload`, {
        filename: file.name || "attachment",
        content_type: file.type || "application/octet-stream",
        file_base64: base64,
        tooth_number: attachmentMeta.tooth_number || null,
        procedure_type: attachmentMeta.procedure_type || null,
        notes: attachmentMeta.notes || null,
      });
      setAttachmentMeta({ tooth_number: "", procedure_type: "", notes: "" });
      loadAttachments();
      showToast({ message: "Image uploaded.", type: "success" });
    } catch (e) {
      showToast({ message: "Failed to upload image.", type: "error" });
    } finally {
      setAttachmentUploading(false);
    }
  };

  const handleAttachmentDelete = async (attachmentId: number) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await apiDelete(`/api/doctor/attachments/${attachmentId}`);
      loadAttachments();
      showToast({ message: "Image deleted.", type: "success" });
    } catch (e) {
      showToast({ message: "Failed to delete image.", type: "error" });
    }
  };

  const handleFinishSession = async (strict: boolean) => {
    if (locking) return;
    if (strict && !allCompleted) return;
    if (!window.confirm(strict ? "Mark visit as finished?" : "Finish this session and send to billing?")) return;
    setLocking(true);
    try {
      await submitRecall();
      await apiPost(`/api/doctor/visit/lock/${visitId}`, {});
      showToast({ message: "Session locked and finalized.", type: "success" });
      if (onLockSuccess) onLockSuccess();
      navigate("/doctor", { replace: true });
    } catch (e) {
      showToast({ message: "Failed to finalize session.", type: "error" });
    } finally {
      setLocking(false);
    }
  };

  const handleMarkCompleted = async () => {
    if (locking) return;
    if (!window.confirm("Mark this visit as completed (without locking)?")) return;
    setLocking(true);
    try {
      await submitRecall();
      await apiPost(`/api/doctor/visit/complete/${visitId}`, {});
      showToast({ message: "Visit marked as completed for billing.", type: "success" });
      if (onLockSuccess) onLockSuccess();
      navigate("/doctor", { replace: true });
    } catch (e) {
      showToast({ message: "Failed to mark visit as completed.", type: "error" });
    } finally {
      setLocking(false);
    }
  };

  if (loading) {
    return (
      <div className="review-loading-state">
        <div className="spinner"></div>
        <p>Verifying Clinical Data...</p>
      </div>
    );
  }

  return (
    <div className="mod-standalone-window review-shell">
      <div className="mod-window-header review-header-bar">
        <div className="review-header-left">
          <h2 className="review-title">
            📋 Review & Finalize
          </h2>
          <p className="mod-text-muted review-subtitle">Verify clinical notes, procedures, and prescriptions before ending the session.</p>
        </div>
        <div className="review-header-actions">
          <Button variant="outline" onClick={() => window.open(`/print?type=prescription&visit=${visitId}`, "_blank")}>
            🖨️ Print Rx
          </Button>
          <Button variant="outline" onClick={() => window.open(`/print?type=doctor&visit=${visitId}`, "_blank")}>
            🖨️ Print Report
          </Button>
        </div>
      </div>

      <div className="review-body">
        <div className="review-main-col">

          {/* Clinical Notes Summary */}
          {(clinicalNotes.complaints || clinicalNotes.habits || clinicalNotes.observation) && (
            <div className="review-card notes-summary-card">
              <div className="card-header">
                <h4>📝 Clinical Notes</h4>
              </div>
              <div className="notes-grid">
                {clinicalNotes.complaints && (
                  <div className="note-item complaints">
                    <span className="note-label">Complaints</span>
                    <p>{clinicalNotes.complaints}</p>
                  </div>
                )}
                {clinicalNotes.habits && (
                  <div className="note-item habits">
                    <span className="note-label">Habits</span>
                    <p>{clinicalNotes.habits}</p>
                  </div>
                )}
                {clinicalNotes.observation && (
                  <div className="note-item observation">
                    <span className="note-label">Observation</span>
                    <p>{clinicalNotes.observation}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Diagnoses Summary */}
          {sessionDiagnoses.length > 0 && (
            <div className="review-card diagnosis-summary-card">
              <div className="card-header">
                <h4>🔍 Diagnoses</h4>
                <span className="badge-count">{sessionDiagnoses.length}</span>
              </div>
              <div className="diagnosis-list">
                {sessionDiagnoses.map((d, i) => (
                  <div key={i} className="diagnosis-tag">
                    {d.code ? `${d.code} - ${d.description}` : d.description}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="review-card imaging-card">
            <div className="card-header">
              <h4>🩻 Imaging</h4>
            </div>
            <div className="imaging-form">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleAttachmentUpload(file);
                  e.currentTarget.value = "";
                }}
                disabled={attachmentUploading}
              />
              <div className="imaging-row">
                <input
                  type="text"
                  placeholder="Tooth # (optional)"
                  value={attachmentMeta.tooth_number}
                  onChange={(e) => setAttachmentMeta((prev) => ({ ...prev, tooth_number: e.target.value }))}
                />
                <input
                  type="text"
                  placeholder="Procedure (optional)"
                  value={attachmentMeta.procedure_type}
                  onChange={(e) => setAttachmentMeta((prev) => ({ ...prev, procedure_type: e.target.value }))}
                />
              </div>
              <textarea
                placeholder="Imaging notes (optional)"
                value={attachmentMeta.notes}
                onChange={(e) => setAttachmentMeta((prev) => ({ ...prev, notes: e.target.value }))}
              />
              {attachmentUploading && <div className="muted">Uploading...</div>}
            </div>

            {attachments.length === 0 ? (
              <div className="empty-state small">No images uploaded.</div>
            ) : (
              <div className="imaging-grid">
                {attachments.map((att) => (
                  <div key={att.id} className="imaging-item">
                    <img src={att.thumbnail_url || att.url} alt={att.filename || "attachment"} />
                    <div className="imaging-meta">
                      <strong>{att.filename || "Image"}</strong>
                      {att.tooth_number && <span>Tooth: {att.tooth_number}</span>}
                      {att.procedure_type && <span>Procedure: {att.procedure_type}</span>}
                      {att.notes && <span className="muted">{att.notes}</span>}
                    </div>
                    <button className="imaging-delete" type="button" onClick={() => handleAttachmentDelete(att.id)}>
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {inProgressItems.length > 0 && (
            <div className="blocker-section">
              <div className="blocker-header">
                <span className="icon">⚠️</span>
                <div>
                  <h4>In Progress Items</h4>
                  <p>Use status buttons to update progress.</p>
                </div>
              </div>
              <div className="blocker-list">
                {inProgressItems.map((r) => (
                  <div key={r.clinical_id} className="blocker-card">
                    <div className="bc-info">
                      <strong>{procLabel(r)}</strong>
                      <span>Tooth: {r.tooth_number || "Gen"}</span>
                      <div className="step-badges">
                        {(r.steps && r.steps.length > 0 ? r.steps : ["No steps"]).map((s, idx) => (
                          <span key={`${r.clinical_id}-step-${idx}`} className="step-badge">
                            {s}
                          </span>
                        ))}
                        <span className="step-badge progress">
                          {r.step_no && r.total_steps ? `Step ${r.step_no}/${r.total_steps}` : "No progress"}
                        </span>
                      </div>
                    </div>
                    <div className="bc-actions">
                      <button
                        className="btn-resolve plan"
                        type="button"
                        onClick={() => resolveBlocker(r.clinical_id, "Planned")}
                      >
                        Mark Planned
                      </button>
                      <button
                        className="btn-resolve progress"
                        type="button"
                        onClick={() => resolveBlocker(r.clinical_id, "In Progress")}
                      >
                        In Progress
                      </button>
                      <button
                        className="btn-resolve done"
                        type="button"
                        onClick={() => resolveBlocker(r.clinical_id, "Completed")}
                      >
                        Mark Done
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="review-card success-border">
            <div className="card-header">
              <h4>Completed Procedures</h4>
              <span className="badge-count">{completedItems.length}</span>
            </div>
            {completedItems.length === 0 ? (
              <div className="empty-state">No procedures performed today.</div>
            ) : (
              <>
                <table className="review-table">
                  <thead>
                    <tr>
                      <th className="review-th review-th-tooth">Tooth</th>
                      <th className="review-th review-th-procedure">Procedure</th>
                      <th className="review-th review-th-steps">Steps</th>
                      <th className="review-th review-th-progress">Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedItems.map((r) => (
                      <tr key={r.clinical_id}>
                        <td>
                          <span className="tooth-pill">{r.tooth_number || "-"}</span>
                        </td>
                        <td><strong>{procLabel(r)}</strong></td>
                        <td className="muted">{r.steps && r.steps.length > 0 ? r.steps.join(", ") : "—"}</td>
                        <td className="muted">
                          {r.step_no && r.total_steps ? `Step ${r.step_no}/${r.total_steps}` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="financial-hint">
                  <span>Estimated Billing Value:</span>
                  <strong>₹ {estimatedTotal}</strong>
                </div>
              </>
            )}
          </div>

          {plannedItems.length > 0 && (
            <div className="review-card info-border">
              <div className="card-header">
                <h4>Planned For Future</h4>
                <span className="badge-count">{plannedItems.length}</span>
              </div>
              <ul className="simple-list">
                {plannedItems.map((r) => (
                  <li key={r.clinical_id}>
                    <span className="bullet">•</span>
                    {procLabel(r)}
                    {r.tooth_number && (
                      <span className="t-ref"> (Tooth {r.tooth_number})</span>
                    )}
                    <div className="step-badges inline">
                      {(r.steps && r.steps.length > 0 ? r.steps : ["No steps"]).map((s, idx) => (
                        <span key={`${r.clinical_id}-planned-step-${idx}`} className="step-badge">
                          {s}
                        </span>
                      ))}
                    </div>
                    <div className="bc-actions inline">
                      <button
                        className="btn-resolve progress"
                        type="button"
                        onClick={() => resolveBlocker(r.clinical_id, "In Progress")}
                      >
                        In Progress
                      </button>
                      <button
                        className="btn-resolve done"
                        type="button"
                        onClick={() => resolveBlocker(r.clinical_id, "Completed")}
                      >
                        Mark Done
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="review-side-col">
          <div className="review-card">
            <div className="card-header">
              <h4>💊 Prescriptions</h4>
            </div>
            {prescriptions.length === 0 ? (
              <div className="empty-state small">No medications prescribed.</div>
            ) : (
              <div className="rx-list">
                {prescriptions.flatMap((p) =>
                  (Array.isArray(p.medicines) ? p.medicines : []).map((m, i) => (
                    <div key={`${p.id}-${i}`} className="rx-item">
                      <div className="rx-name">{m.drug_name || m.name || "Unnamed"}</div>
                      <div className="rx-detail">
                        {(m.frequency || m.dosage || "—")} • {m.days || "—"} days
                      </div>
                      {m.instructions && (
                        <div className="rx-detail muted">&quot;{m.instructions}&quot;</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="review-card labwork-card">
            <div className="card-header">
              <h4>🧪 Labwork Status</h4>
            </div>
            <div className="labwork-form">
              <div className="labwork-row">
                <select
                  value={labForm.lab_id}
                  onChange={(e) => setLabForm((prev) => ({ ...prev, lab_id: e.target.value }))}
                >
                  <option value="">Select Lab</option>
                  {labs.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
                <select
                  value={labForm.work_id}
                  onChange={(e) => setLabForm((prev) => ({ ...prev, work_id: e.target.value }))}
                >
                  <option value="">Work Type</option>
                  {labWorks.map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>
              <div className="labwork-row">
                <input
                  type="date"
                  value={labForm.impression_date}
                  onChange={(e) => setLabForm((prev) => ({ ...prev, impression_date: e.target.value }))}
                  placeholder="Impression date"
                />
                <input
                  type="date"
                  value={labForm.eta_date}
                  onChange={(e) => setLabForm((prev) => ({ ...prev, eta_date: e.target.value }))}
                  placeholder="ETA"
                />
              </div>
              <Button variant="outline" onClick={handleCreateLabOrder} isLoading={labSaving}>
                Add Lab Order
              </Button>
            </div>

            {labOrders.length === 0 ? (
              <div className="empty-state small">No lab orders yet.</div>
            ) : (
              <div className="labwork-list">
                {labOrders.map((o) => (
                  <div key={o.id} className="labwork-item">
                    <div className="labwork-info">
                      <strong>{o.work_name || "Lab Work"}</strong>
                      <span>{o.lab_name || "Lab"}</span>
                      <span className="muted">ETA: {o.eta_date ? new Date(o.eta_date).toLocaleDateString() : "—"}</span>
                    </div>
                    <div className="labwork-actions">
                      <span className={`lab-status ${String(o.status || "").toLowerCase()}`}>{o.status || "SENT"}</span>
                      {String(o.status || "").toUpperCase() !== "RECEIVED" && (
                        <button className="lab-recv" type="button" onClick={() => handleMarkReceived(o.id)}>
                          Mark Received
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="review-card signoff-card">
            <div className="card-header">
              <h4>✅ Treatment Sign-off</h4>
            </div>
            {signoff?.signed_at ? (
              <div className="signoff-status">
                <div><strong>Signed by:</strong> {signoff.signed_by || "Doctor"}</div>
                <div className="muted">{new Date(signoff.signed_at).toLocaleString()}</div>
                {signoff.notes && <div className="signoff-notes">&quot;{signoff.notes}&quot;</div>}
              </div>
            ) : (
              <div className="signoff-status muted">Not signed yet.</div>
            )}
            <textarea
              className="signoff-input"
              placeholder="Sign-off notes (optional)"
              value={signoffNotes}
              onChange={(e) => setSignoffNotes(e.target.value)}
            />
            <Button variant="primary" onClick={handleSignoff} isLoading={signing}>
              {signoff?.signed_at ? "Re-sign" : "Sign Off"}
            </Button>
          </div>

          <div className="review-card comms-card">
            <div className="card-header">
              <h4>📞 Communication Log</h4>
            </div>
            <div className="comms-form">
              <div className="comms-row">
                <select
                  value={commForm.channel}
                  onChange={(e) => setCommForm((prev) => ({ ...prev, channel: e.target.value }))}
                >
                  <option value="CALL">Call</option>
                  <option value="SMS">SMS</option>
                  <option value="EMAIL">Email</option>
                  <option value="WHATSAPP">WhatsApp</option>
                  <option value="OTHER">Other</option>
                </select>
                <select
                  value={commForm.direction}
                  onChange={(e) => setCommForm((prev) => ({ ...prev, direction: e.target.value }))}
                >
                  <option value="OUT">Outbound</option>
                  <option value="IN">Inbound</option>
                </select>
              </div>
              <input
                type="text"
                placeholder="Subject"
                value={commForm.subject}
                onChange={(e) => setCommForm((prev) => ({ ...prev, subject: e.target.value }))}
              />
              <textarea
                placeholder="Notes"
                value={commForm.notes}
                onChange={(e) => setCommForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
              <Button variant="outline" onClick={handleAddCommunication} isLoading={commSaving}>
                Add Log
              </Button>
            </div>
            {commLogs.length === 0 ? (
              <div className="empty-state small">No communication logs.</div>
            ) : (
              <div className="comms-list">
                {commLogs.map((log) => (
                  <div key={log.id} className="comms-item">
                    <div className="comms-meta">
                      <span className="badge">{log.channel || "CALL"}</span>
                      <span className="badge subtle">{log.direction || "OUT"}</span>
                      <span className="muted">{log.created_at ? new Date(log.created_at).toLocaleString() : ""}</span>
                    </div>
                    {log.subject && <div className="comms-subject">{log.subject}</div>}
                    {log.notes && <div className="comms-notes">{log.notes}</div>}
                    {log.created_by_name && (
                      <div className="comms-by muted">by {log.created_by_name}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="next-visit-hint">
            <label>Next Recall:</label>
            <input
              type="date"
              className="recall-select"
              value={recallDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRecallDate(e.target.value)}
            />
            <input
              type="text"
              className="recall-reason"
              placeholder="Reason (optional)"
              value={recallReason}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRecallReason(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="review-footer-bar">
        <div className="review-footer-status">
          <span className={`review-footer-text ${allCompleted ? "is-complete" : ""}`}>
            {allCompleted ? "✓ All steps completed." : "ℹ️ Session can be finished anytime."}
          </span>
        </div>
        
        <div className="review-footer-actions">
          <Button variant="ghost" onClick={onModifyChart}>Modify Chart</Button>
          
          <Button variant="outline" onClick={handleMarkCompleted} isLoading={locking}>
             Mark Completed (For Billing)
          </Button>
          
          <Button variant="secondary" onClick={() => handleFinishSession(false)} isLoading={locking}>
             Save & Close (Draft)
          </Button>
          
          <Button variant="primary" onClick={() => handleFinishSession(true)} disabled={!allCompleted || locking} isLoading={locking}>
             Finalize & Lock Chart
          </Button>
        </div>
      </div>
    </div>
  );
}
